import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { format } from 'date-fns';
import fontColorContrast from 'font-color-contrast';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import * as React from 'react';
import { Fragment } from 'react';
import { LayoutAnimation, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  CREATE_ASSESSMENT_STEP1,
  LOAD_ASSESSMENT,
} from 'store/assessments/actions';
import { selectAsset } from 'store/assets/selectors';
import { AssessmentResults } from 'store/assets/typesAse';
import { selectOrganisation } from 'store/organisations/selectors';
import { AssessmentStatusesEnum } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSET],
  Screens.ASSET_COMPLIANCE
>;

interface AssessmentDates {
  details: Pick<AssessmentResults['details'], 'assessedDate' | 'assessedTime'>;
}
const createAssessedTimestamp = <T extends AssessmentDates>(
  assessment: T,
): number =>
  new Date(
    `${assessment.details.assessedDate}T${assessment.details.assessedTime}`,
  ).getTime();

export const ComplianceSummaryScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const { assetId, complianceTypeId } = route.params;
  const [showRiskRating, setShowRiskRating] = React.useState<boolean>(false);

  const screenSize = useScreenSize();
  const asset = useSelector((state) => selectAsset(state, assetId))!;

  const organisation = useSelector((state) =>
    selectOrganisation(state, asset.organisation_id),
  );

  const complianceType = organisation.compliance_types[complianceTypeId]!;

  const rows = naturalOrderBy(
    Object.values(organisation.compliance_subtypes).filter(
      (complianceSubtype) =>
        complianceSubtype.compliance_type_id === complianceType.id &&
        (!asset.assessmentTypesVisible ||
          asset.assessmentTypesVisible.includes(complianceSubtype.id)),
    ),
    (v) => v.name,
  ).map((complianceSubtype) => {
    const assessments = (
      Object.values(asset.assessments).filter(
        (assessment) => !!assessment,
      ) as AssessmentResults[]
    )
      .filter(
        (assessment) =>
          assessment.complianceId === complianceSubtype.id &&
          (assessment.details.statusId !== 'approved' ||
            assessment.latestApproved),
      )
      .sort((prev, next) =>
        createAssessedTimestamp(prev) > createAssessedTimestamp(next) ? -1 : 1,
      );
    const dueDate =
      asset.compliance_summary[complianceSubtype.id]?.due_date ?? undefined;
    const isAssigned =
      !!asset.compliance_summary[complianceSubtype.id]?.assigned;
    return {
      assessmentRows: assessments.map((assessment) => {
        const assessedBy = assessment.details.assessedBy
          .map((ab) => ab.name)
          .join(', ');
        const templateName =
          assessment.details.statusId === AssessmentStatusesEnum.approved
            ? complianceSubtype.name
            : organisation.assessmentTemplates[assessment.templateId]?.name ||
              complianceSubtype.name;
        const templateTitle =
          organisation.assessmentTemplates[assessment.templateId]?.title ||
          assessment.details.title ||
          complianceSubtype.name;
        const finalTemplateName = [
          templateName !== complianceSubtype.name ? templateName : '',
          templateTitle !== complianceSubtype.name &&
          templateTitle !== templateName
            ? templateTitle
            : '',
        ]
          .filter(filterArrayEmpty)
          .join('\n');
        return {
          assessmentId: assessment.id,
          color: `#${
            organisation.assessment_statuses[assessment.details.statusId]!
              .colour
          }`,
          label:
            organisation.assessment_statuses[assessment.details.statusId]!.name,
          value: `${assessment.statusSummaryLabel}${
            assessedBy ? ` by ${assessedBy}` : ''
          }`,
          templateName: finalTemplateName,
        };
      }),
      createAssessmentRow: Object.values(organisation.assessmentTemplates).some(
        (template) =>
          !template.deleted &&
          template.compliance_subtype_id === complianceSubtype.id,
      )
        ? {
            assetId,
            color: `#${organisation.assessment_statuses.notstarted!.colour}`,
            complianceSubtypeId: complianceSubtype.id,
            complianceTypeId,
            label: `Create ${complianceSubtype.name}`,
          }
        : null,
      description: dueDate
        ? `Due ${format(new Date(dueDate), UI_DATE_FORMAT_LONG)}${
            isAssigned ? " • You're Assigned" : ''
          }`
        : 'No Due Date Set',
      id: complianceSubtype.id,
      taskDescriptionRow:
        asset.compliance_summary[complianceSubtype.id]?.description,
      title: complianceSubtype.name,
    };
  });

  const rating = asset.assessment_risk_ratings[complianceTypeId];
  const { matrix } = complianceType;
  let risk = null;
  if (rating && matrix) {
    const [likelihoodId, severityId, reason = ''] = rating;
    const matrixRating =
      matrix.ratings[matrix.outcomes[`${likelihoodId}x${severityId}`]!];
    risk = {
      label: `${complianceType.name} Risk Rating`,
      likelihoodDescription:
        matrix.likelihoods[likelihoodId]?.description || 'No Description',
      likelihoodLabel: matrix.likelihoods[likelihoodId]?.name || '',
      ratingColor: matrixRating ? `#${matrixRating.bcolour}` : '',
      ratingColorFg: matrixRating
        ? fontColorContrast(`#${matrixRating.bcolour}`)
        : '',
      ratingDescription: matrixRating?.description || 'No Description',
      ratingLabel: matrixRating?.name || '',
      reason,
      severityDescription:
        matrix.severities[severityId]?.description || 'No Description',
      severityLabel: matrix.severities[severityId]?.name || '',
    };
  }

  return (
    <PageScrollView
      isInModal={false}
      header={
        <NavigationHeader
          heading={complianceType.name}
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
        />
      }
    >
      {risk && (
        <>
          <View
            style={{
              paddingTop: theme.paddingVertical / 2,
            }}
          >
            <View
              style={{
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  alignSelf: 'center',
                  backgroundColor: '#f1f1f1',
                  borderColor: '#f1f1f1',
                  borderRadius: 8,
                  borderWidth: 1,
                  marginHorizontal: theme.paddingHorizontal,
                  shadowColor: 'black',
                  shadowOffset: { height: 0, width: 0 },
                  shadowOpacity: 0.1,
                  shadowRadius: 10,
                }}
              >
                <View
                  style={{
                    borderRadius: 8,
                    flexDirection: 'row',
                    overflow: 'hidden',
                  }}
                >
                  {risk.ratingLabel ? (
                    <>
                      <Txt
                        numberOfLines={1}
                        style={{
                          backgroundColor: 'white',
                          paddingHorizontal: theme.paddingHorizontal,
                          paddingVertical: theme.paddingHorizontal,
                          textAlign: 'center',
                        }}
                      >
                        {risk.likelihoodLabel}
                      </Txt>
                      <Txt
                        numberOfLines={1}
                        style={{
                          backgroundColor: 'white',
                          marginHorizontal: 1,
                          paddingHorizontal: theme.paddingHorizontal,
                          paddingVertical: theme.paddingHorizontal,
                          textAlign: 'center',
                        }}
                      >
                        {risk.severityLabel}
                      </Txt>
                      <Txt
                        numberOfLines={1}
                        style={{
                          backgroundColor: risk.ratingColor,
                          color: risk.ratingColorFg,
                          paddingHorizontal: theme.paddingHorizontal,
                          paddingVertical: theme.paddingHorizontal,
                          textAlign: 'center',
                        }}
                      >
                        {risk.ratingLabel}
                      </Txt>
                    </>
                  ) : (
                    <Txt
                      numberOfLines={1}
                      style={{
                        backgroundColor: '#FFFFFF',
                        paddingHorizontal: theme.paddingHorizontal,
                        paddingVertical: theme.paddingHorizontal,
                        textAlign: 'center',
                      }}
                    >
                      No Rating
                    </Txt>
                  )}
                </View>
              </View>
              <View
                style={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  paddingTop: theme.grid,
                }}
              >
                <Txt size="xsmall" style={{ color: theme.colors.textBlack64 }}>
                  Likelihood x Severity = Rating{'  '}
                </Txt>
                <Button
                  buttonTheme={ButtonTheme.secondary}
                  text={showRiskRating ? 'See Less' : 'See More'}
                  uppercase={false}
                  compact
                  textStyle={{ minWidth: 70 }}
                  onPress={() => {
                    LayoutAnimation.configureNext(
                      LayoutAnimation.Presets.easeInEaseOut,
                    );
                    setShowRiskRating((show) => !show);
                  }}
                />
              </View>
            </View>
            {showRiskRating && (
              <Card margin={false} marginTop>
                {!!risk.ratingLabel && (
                  <CardRow
                    labelAlignTop
                    label={`Likelihood: ${risk.likelihoodLabel}`}
                    value={risk.likelihoodDescription}
                  />
                )}
                {!!risk.ratingLabel && (
                  <CardRow
                    labelAlignTop
                    label={`Severity: ${risk.severityLabel}`}
                    value={risk.severityDescription}
                  />
                )}
                {!!risk.ratingLabel && (
                  <CardRow
                    labelAlignTop
                    label={`Rating: ${risk.ratingLabel}`}
                    value={risk.ratingDescription}
                  />
                )}
                {!!risk.reason && (
                  <CardRow
                    labelAlignTop
                    label="Reason for Rating"
                    value={risk.reason}
                  />
                )}
              </Card>
            )}
          </View>
          <SeparatorHorizontal marginVertical={theme.paddingVertical} />
        </>
      )}

      <ViewWithSeparators separator={<SeparatorHorizontal withMarginBottom />}>
        {rows.map(
          ({
            assessmentRows,
            createAssessmentRow,
            description,
            id,
            taskDescriptionRow,
            title,
          }) => (
            <Fragment key={id}>
              <CardGeneric>
                <Txt size="large" weight="bold">
                  {title}
                </Txt>
                {!!description && <Txt>{description}</Txt>}
              </CardGeneric>
              <Card>
                {!!taskDescriptionRow && (
                  <CardRow
                    labelAlignTop
                    label="Notes for the Assessor"
                    value={taskDescriptionRow}
                  />
                )}
                {assessmentRows.length
                  ? assessmentRows.map((row) => (
                      <CardRow
                        key={row.assessmentId}
                        labelAlignTop
                        label={row.label}
                        label2={row.templateName}
                        value={row.value}
                        icon={ICONS.FileDuotone}
                        iconColor={row.color}
                        navigationType="modal"
                        onPress={() =>
                          dispatch(
                            LOAD_ASSESSMENT.request({
                              assessmentId: row.assessmentId,
                              assetId,
                            }),
                          )
                        }
                      />
                    ))
                  : !taskDescriptionRow && (
                      <CardRow labelAlignTop value="No Assessments" />
                    )}
                {createAssessmentRow && (
                  <CardRow
                    labelAlignTop
                    label={createAssessmentRow.label}
                    iconColor={createAssessmentRow.color}
                    icon={ICONS.PlusLight}
                    onPress={() =>
                      dispatch(
                        CREATE_ASSESSMENT_STEP1({
                          assetId: createAssessmentRow.assetId,
                          complianceSubtypeId:
                            createAssessmentRow.complianceSubtypeId,
                        }),
                      )
                    }
                  />
                )}
              </Card>
            </Fragment>
          ),
        )}
      </ViewWithSeparators>
    </PageScrollView>
  );
};
